import { Controller, type FieldPath, type FieldValues } from 'react-hook-form';

import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { MultiSelect } from '@/components/ui/multi-select';

import { cn } from '@/lib/utils';
import { type CommonFormFieldProps, lgSpan, mdSpan, smSpan } from './form-input';

type Option = {
  label: string;
  value: string | number;
};

type FormMultiSelectProps<T extends FieldValues = FieldValues> =
  CommonFormFieldProps<T> & {
    options: Option[];
    placeholder?: string;
    disabled?: boolean;
  };

export function FormMultiSelect<T extends FieldValues>({
  name,
  control,
  label,
  options = [],
  placeholder,

  sm = 12,
  md = 4,
  lg = 4,

  required,
  disabled,
  className,
}: FormMultiSelectProps<T>) {
  return (
    <Controller
      name={name as FieldPath<T>}
      control={control}
      render={({ field, fieldState }) => (
        <Field
          className={cn(smSpan[sm], mdSpan[md], lgSpan[lg], className)}
          data-invalid={fieldState.invalid}
        >
          <FieldLabel htmlFor={name}>
            {label} {required && <strong className="text-red-500">*</strong>}
          </FieldLabel>

          <MultiSelect<Option>
            label={label}
            options={options}
            getOptionValue={(option) => option.value}
            getDisplayValue={(option) => option.label}
            placeholder={placeholder}
            value={field.value ?? []}
            onChange={(values) => field.onChange(values)}
            disabled={disabled || field.disabled}
          />

          {fieldState.error && <FieldError errors={[fieldState.error]} />}
        </Field>
      )}
    />
  );
}
